import { useState } from "react";
import { Users, Activity, AlertTriangle, TrendingUp } from "lucide-react";
import SectionCard from "@/components/SectionCard";
import KpiCard from "@/components/KpiCard";
import BarChart from "@/components/charts/BarChart";
import CohorteDetailPanel from "@/components/CohorteDetailPanel";
import { cohorteData, cohorteDesgloseData, DATOS_POR_PERIODO } from "@/mocks/data";
import { clasificarCohorte, obtenerDesglose } from "@/lib/motorSegmentacion";
import { useFilters } from "@/context/FiltersContext";
import { formatNum } from "@/lib/constants";

export default function Cohorte() {
  const { filters } = useFilters();
  const [selectedEtapa, setSelectedEtapa] = useState(null);

  // Datos reales del período
  const periodoData = DATOS_POR_PERIODO[filters.periodo] || DATOS_POR_PERIODO["Abril 2026"];

  // Ratio real basado en DG episodios del período vs abril base
  const ratio = periodoData.dg.episodios / 123718;
  const escalar = (val) => val != null ? Math.round(val * ratio) : 0;

  // Clasificar cohorte con ratio real (no applyFilters)
  const { etapas, kpis } = clasificarCohorte(cohorteData, filters, escalar);

  // Obtener desglose si hay etapa seleccionada
  const desglose = selectedEtapa
    ? obtenerDesglose(selectedEtapa, cohorteDesgloseData, filters, escalar)
    : null;

  // Preparar datos para BarChart
  const chartCategories = etapas.map((e) => e.etapa.replace(/_/g, " "));
  const chartData = etapas.map((e) => e.pacientes);
  const chartColors = etapas.map((e) => (e.alerta ? "#EF4444" : e.color));

  const handleBarClick = (event, chartContext, config) => {
    const idx = config.dataPointIndex;
    if (idx >= 0 && idx < etapas.length) {
      const etapa = etapas[idx].etapa;
      setSelectedEtapa(selectedEtapa === etapa ? null : etapa);
    }
  };

  return (
    <div className="space-y-6" data-testid="cohorte-page">
      {/* Hero */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Segmentación de Cohorte</h1>
        <p className="text-sm text-slate-500 mt-1">
          Distribución de pacientes por etapa actual del viaje ambulatorio — {filters.periodo}
        </p>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          testid="kpi-total-pacientes"
          label="Total pacientes"
          value={formatNum(kpis.totalPacientes)}
          icon={Users}
          accent="violet"
        />
        <KpiCard
          testid="kpi-activos"
          label="En etapas activas"
          value={formatNum(kpis.activos)}
          icon={Activity}
          accent="blue"
        />
        <KpiCard
          testid="kpi-en-fuga"
          label="En riesgo de fuga"
          value={formatNum(kpis.enFuga)}
          icon={AlertTriangle}
          accent="rose"
        />
        <KpiCard
          testid="kpi-mayor-acumulacion"
          label="Mayor acumulación"
          value={kpis.etapaMayorAcumulacion.replace(/_/g, " ")}
          icon={TrendingUp}
          accent="amber"
        />
      </div>

      {/* Alertas de etapas con alto % detenidos */}
      {etapas.filter((e) => e.alerta).length > 0 && (
        <div className="flex flex-wrap gap-2">
          {etapas.filter((e) => e.alerta).map((e) => (
            <div
              key={e.etapa}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-rose-50 border border-rose-200"
            >
              <AlertTriangle className="w-3 h-3 text-rose-500" />
              <span className="text-xs font-semibold text-rose-700">
                {e.etapa.replace(/_/g, " ")}: {e.pctDetenidos}% detenidos &gt;30d
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Gráfico de barras horizontales */}
      <SectionCard
        title="Pacientes por Etapa Actual"
        subtitle="Clic en una barra para ver el desglose detallado"
        testid="cohorte-bar-section"
      >
        <BarChart
          categories={chartCategories}
          series={[{ name: "Pacientes", data: chartData }]}
          colors={chartColors}
          height={Math.max(400, etapas.length * 48)}
          horizontal
          distributed
        />

        {/* Tabla resumen debajo del gráfico */}
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-slate-100">
                <th className="text-left py-2 px-2 font-semibold text-slate-500">Etapa</th>
                <th className="text-right py-2 px-2 font-semibold text-slate-500">Pacientes</th>
                <th className="text-right py-2 px-2 font-semibold text-slate-500">% Total</th>
                <th className="text-right py-2 px-2 font-semibold text-slate-500">Detenidos 30d</th>
                <th className="text-right py-2 px-2 font-semibold text-slate-500">% Detenidos</th>
              </tr>
            </thead>
            <tbody>
              {etapas.map((e) => (
                <tr
                  key={e.etapa}
                  onClick={() => setSelectedEtapa(selectedEtapa === e.etapa ? null : e.etapa)}
                  className={`border-b border-slate-50 cursor-pointer transition hover:bg-violet-50 ${
                    selectedEtapa === e.etapa ? "bg-violet-50" : ""
                  } ${e.alerta ? "border-l-2 border-l-red-400" : ""}`}
                  data-testid={`row-${e.etapa}`}
                >
                  <td className="py-2 px-2 font-medium text-slate-900 flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full" style={{ backgroundColor: e.color }} />
                    {e.etapa.replace(/_/g, " ")}
                    {e.alerta && <AlertTriangle className="w-3 h-3 text-red-500" />}
                  </td>
                  <td className="py-2 px-2 text-right tabular-nums text-slate-700">{formatNum(e.pacientes)}</td>
                  <td className="py-2 px-2 text-right tabular-nums text-slate-500">{e.pctTotal}%</td>
                  <td className="py-2 px-2 text-right tabular-nums text-slate-700">{formatNum(e.detenidos)}</td>
                  <td className={`py-2 px-2 text-right tabular-nums font-semibold ${e.alerta ? "text-red-600" : "text-slate-500"}`}>
                    {e.pctDetenidos}%
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </SectionCard>

      {/* Panel de detalle — drawer lateral */}
      {selectedEtapa && (
        <CohorteDetailPanel
          etapa={selectedEtapa}
          desglose={desglose}
          etapaData={etapas.find((e) => e.etapa === selectedEtapa)}
          onClose={() => setSelectedEtapa(null)}
        />
      )}
    </div>
  );
}
